// NJORD — inngangspunktet. Binder kilde → lager → skjermer.
//
// ARKITEKTUREN I ÉN SETNING: én uforanderlig tilstand i `lager.ts`, én kilde som
// leverer meldinger, og skjermer som tegner seg helt om når tilstanden endres.
//
// HVORFOR FULL OMTEGNING OG IKKE FINKORNET OPPDATERING:
// alt i NJORD er tidsavhengig. Sensorenes friskhet, målingenes alder,
// trusselrangeringen — alle endres uten at det kommer én ny melding. En
// finkornet «oppdater bare det som endret seg»-modell måtte da likevel tegnet om
// nesten alt hvert sekund, men med risiko for at ÉN rad glemmes og viser gammel
// alder ved siden av ny. På en post der hele poenget er å stole på alderstallene,
// er det den verste feilen. Omtegningen er billig: fire sensorrader og en
// kontaktliste på titalls rader.
//
// ÉN KLOKKE PER TEGNING (`naa()` i konteksten). Hentes klokka i hver komponent,
// kan to rader vise ulik alder på samme måling.
// STILARKENE MÅ IMPORTERES HERFRA. Vite tar bare med CSS som er importert
// fra JS — en fil som bare ligger i src/ havner ALDRI i bygget, og det finnes
// ingen <link> i index.html.
//
// DETTE VAR EN EKTE FEIL, og den var usynlig for gaten: bygget gikk grønt,
// testene gikk grønt, og appen ble levert som ustilt HTML med serif-font og
// alt stablet loddrett. `tokens.test.ts` fanget den ikke fordi den leser
// CSS-filene fra DISK med readFileSync — den sier at tokenene er riktige, ikke
// at nettleseren noen gang får dem. Se `stilark.test.ts`, som nå sjekker det.
//
// theme.css FØRST: den definerer tokenene style.css bruker.
import './theme.css';
import './style.css';

import { KOP_WS, lagKilde, type Kilde, type Tilkobling } from './api/kilde.js';
import { ovelsemeldinger, ovelsepuls } from './api/ovelse.js';
import {
  SENSORART_NAVN,
  type KopKommando,
  type KopMelding,
  type Operatorflagg,
} from './api/typer.js';
import { artOppslag, bruk, settFlagg, tomtLager, type Lagertilstand } from './lib/lager.js';
// Skallet, tomtilstandene, panelmalen, friskhetsmodellen og ØVINGSKILDEN er
// FLATENS — delt med ULL gjennom `@skade/sensorflate`. Alt som er importert fra
// `./lib/` og `./api/` under er kystspesifikt.
import {
  FRISKHET_NAVN,
  NIVAA_NAVN,
  div,
  formaterAlder,
  formaterKlokke,
  knapp,
  lagKunngjører,
  lagOvelseskilde,
  lagPanel,
  lesOvelsevalg,
  manglerKilde,
  merke,
  montérPoppetPanel,
  nivaaKlasse,
  ovelseAvgjort,
  ovelsebanner,
  poppetPanel,
  postTillit,
  sensorStatus,
  sett,
  settFlatenavn,
  settOvelsesramme,
  skrivOvelsevalg,
  span,
  type PostTillit,
  statusprikk,
  tomt,
  utgaaendeNektet,
} from '@skade/sensorflate';
import {
  sorterPaaTrussel, trusselband, TRUSSELBAND_NAVN, type Trusselvurdering,
} from './lib/trussel.js';
import { ikon } from './ui/ikoner.js';
import { tegnKontaktkort } from './skjermer/kontaktkort.js';
import type { Kontekst, Okt } from './skjermer/kontekst.js';

const OVELSE_NØKKEL = 'njord.ovelse';

/**
 * Rolle fra URL er et UTVIKLINGSHJELPEMIDDEL, aldri en innlogging.
 *
 * NJORD har ingen autentiseringstjeneste ennå. Alternativet til dette ville vært
 * å la alle knapper være aktive, altså å late som operatøren har alle
 * rettigheter — og det er verre enn å ikke ha rettigheter i det hele tatt, fordi
 * det først oppdages når en handling avvises av hubben. `opphav` bæres med og
 * vises i toppstripa, så en satt rolle aldri kan forveksles med en ekte.
 */
function lesØkt(søk = location.search): Okt {
  const rolle = new URLSearchParams(søk).get('rolle');
  if (!rolle) return { bruker: null, opphav: 'ingen' };
  return {
    bruker: { id: `dev:${rolle}`, role: rolle as never, org: {} } as never,
    opphav: 'url-parameter',
  };
}

function start(): void {
  const rot = document.getElementById('app');
  if (!rot) throw new Error('NJORD: #app finnes ikke i index.html');

  // Navnet panelmalen åpner pop-out-vinduer under (`njord-<panel>`). Settes her
  // fordi malen er delt: to flater fra samme pakke som popper ut «kart» skal
  // ikke overta hverandres vindu.
  settFlatenavn('njord');

  let tilstand: Lagertilstand = tomtLager();
  let valgtId: string | null = null;
  const lyttere = new Set<() => void>();
  const kunngjører = lagKunngjører();

  // ── ØVINGSDATA ────────────────────────────────────────────────────────────
  // Porten avgjør, ikke ønsket: `ovelseAvgjort` slår sammen «har operatøren bedt
  // om øving?» og «får denne flata i det hele tatt øve?». Er `VITE_KOP_WS` satt,
  // er svaret NEI uansett hva som står i URL-en eller i nettleserlagringen — se
  // krav 3 i `@skade/sensorflate/ovelse.ts`. Håndhevet i koden: kilden under
  // KASTER om den konstrueres med en broadresse.
  const ovelsevedtak = ovelseAvgjort({
    broAdresse: KOP_WS,
    søk: location.search,
    lagret: lesOvelsevalg(OVELSE_NØKKEL),
  });
  const ovelse = ovelsevedtak.på;

  // Klokka settes ÉN gang per tegning, i `tegn()`. Alle komponenter leser den
  // samme verdien gjennom `ctx.naa()`.
  let naaMs = Date.now();

  /**
   * Kilden. Øvingskilden er den DELTE (`lagOvelseskilde`) — bare manuset er
   * NJORDs. Den later ALDRI som den er tilkoblet: `tilkobling()` melder
   * `ovelse`, og toppstripa, banneret og ramma sier det høyt. Øvingsdata som ikke
   * er merket er en løgn om hva operatøren ser.
   *
   * `pulsMs` er 4 s (pakkens standard): raskt nok til at aldrene beveger seg mens
   * man ser på, sakte nok til at man rekker å lese en rad før den endrer seg.
   */
  const kilde: Kilde = ovelse
    ? lagOvelseskilde<KopMelding>({
        broAdresse: KOP_WS,
        start: ovelsemeldinger,
        puls: ovelsepuls,
      })
    : lagKilde(KOP_WS);

  /** Slå øvingen av eller på, og last flata på nytt med et rent lager. */
  function settOvelse(på: boolean): void {
    skrivOvelsevalg(OVELSE_NØKKEL, på ? '1' : '0');
    // Omlasting og ikke en tilstandsbryter: å bytte kilde under drift ville
    // etterlatt øvingskontakter i lageret ved siden av ekte målinger, og DA er
    // merkingen det eneste som skiller dem. Et rent lager er billigere å stole på.
    location.search = på ? '?ovelse=1' : '?ovelse=0';
  }

  const ctx: Kontekst = {
    tilstand: () => tilstand,
    naa: () => naaMs,
    tilkobling: () => kilde.tilkobling(),
    okt: () => lesØkt(),
    valgt: () => tilstand.kontakter.find((k) => k.id === valgtId) ?? null,
    velg(id) {
      valgtId = id;
      tegn();
    },
    settFlagg(kontaktId, flagg: Operatorflagg) {
      tilstand = settFlagg(tilstand, kontaktId, flagg);
      tegn();
    },
    /**
     * Utgående kommando. VAKTA SPØRRES FØRST, alltid.
     *
     * `utgaaendeNektet` er krav 4 fra `ovelse.ts`: ingenting fra en øving skal
     * kunne nå SKADE. Den nekter av to uavhengige grunner — at øvingen pågår (alt
     * på flata er da oppdiktet), og at kommandoen SELV bærer øvingsflagget et
     * sted nede i strukturen. Øvingskildens `send()` returnerer allerede `false`,
     * men det er ikke godt nok som eneste vern: en `false` sier «gikk ikke ut nå»,
     * som operatøren rimeligvis leser som «prøv igjen». Nekten sier hvorfor.
     */
    send(k: KopKommando) {
      const nektet = utgaaendeNektet({ ovelseAktiv: ovelse, kommando: k });
      if (nektet) {
        kunngjører.si(nektet);
        return false;
      }
      const ok = kilde.send(k);
      if (!ok) kunngjører.si('Kommandoen ble ikke sendt — ikke tilkoblet broen.');
      return ok;
    },
    kunngjør: (m) => kunngjører.si(m),
    påEndring(h) {
      lyttere.add(h);
      return () => lyttere.delete(h);
    },
  };

  // ── Skjelett ──────────────────────────────────────────────────────────────

  const topp = div('topp');
  const toppPost = span('topp-post');
  const toppTilkobling = span();
  const toppKlokke = span('topp-klokke');
  topp.append(span('topp-merke', 'NJORD'), toppPost);
  const toppHøyre = div('topp-hoyre');
  // Øvingsknappen i toppstripa I TILLEGG til den i tomtilstanden: sensorpanelet
  // kan lukkes, og da forsvinner den eneste veien inn i en øving. Den finnes bare
  // når porten faktisk tillater øving — se `ovelseAvgjort` over. Er øvingen PÅ,
  // står av-knappen i banneret, og to av-knapper ville vært to steder å lete.
  if (!ovelse && ovelsevedtak.nektet === null) {
    toppHøyre.append(knapp('Øvingsdata', () => settOvelse(true), {
      variant: 'stille',
      tittel: 'Fyll flata med et oppdiktet, tydelig merket øvingsbilde. Kan ikke sende noe til SKADE.',
    }));
  }
  toppHøyre.append(toppTilkobling, toppKlokke);
  topp.append(toppHøyre);

  const flate = div('flate');
  const omrSensorer = div('omr-sensorer');
  const omrKart = div('omr-kart');
  const omrBunn = div('omr-bunn');
  const omrKontakter = div('omr-kontakter');
  flate.append(omrSensorer, omrKart, omrBunn, omrKontakter);

  const sensorpanel = lagPanel({ nokkel: 'sensorer', tittel: 'Sensorer', kanLosnes: true });
  const kartpanel = lagPanel({ nokkel: 'kart', tittel: 'Sjøkart', kanLukkes: false, kanLosnes: false });
  const bunnpanel = lagPanel({ nokkel: 'bunn', tittel: 'Kamera og spektrogram', kanLosnes: true });
  const listepanel = lagPanel({ nokkel: 'kontakter', tittel: 'Kontakter', kanLosnes: true });
  /**
   * Kortpanelet er en UNDER-boks av kontaktlista, og vises bare når det finnes en
   * kontakt å ha et kort om (FUNN C, se `tegnKontaktliste`).
   *
   * `kortLukket` finnes fordi den regelen ellers ville overkjørt operatøren: har
   * han trykket `×` på kortet, skal det BLI lukket — også når neste kontakt kommer
   * inn. Et panel som åpner seg selv igjen er et panel man slutter å lukke.
   */
  let kortLukket = false;
  const kortpanel = lagPanel({
    nokkel: 'kontaktkort',
    tittel: 'Kontaktkort',
    kanLosnes: true,
    paaLukk: () => { kortLukket = true; },
  });

  omrSensorer.append(sensorpanel.rot);
  omrKart.append(kartpanel.rot);
  omrBunn.append(bunnpanel.rot);
  omrKontakter.append(listepanel.rot, kortpanel.rot);

  rot.append(topp, flate, kunngjører.rot);

  // ── DEN PERMANENTE ØVINGSMERKINGEN (krav 2) ──────────────────────────────
  // To merker, ikke ett: BANNERET øverst sier hva som er galt, RAMMA rundt hele
  // viewporten gjør at også et UTSNITT av skjermen bærer merket — og et utsnitt
  // er nettopp det folk limer inn i en rapport. Banneret kan ikke lukkes; knappen
  // slår av øvingen. Se `ovelsebanner`/`settOvelsesramme`.
  const banner = ovelse
    ? ovelsebanner(
        () => settOvelse(false),
        'ØVINGSDATA — IKKE EKTE DATA. Kontaktene, sensorene og målingene under er oppdiktet, '
        + 'og ingenting kan sendes til SKADE så lenge dette står på.',
      )
    : null;
  settOvelsesramme(ovelse);
  if (banner) rot.insertBefore(banner, flate);

  // Pop-out: ett panel alene i vinduet. Ekte `window.open`, ikke et falskt lag.
  // Monteringen bor i panelmalen (`montérPoppetPanel`) fordi den var ordrett lik
  // i ULL. `behold` er ikke pynt: uten banneret ville et poppet vindu fullt av
  // øvingsdata stått uten sitt viktigste merke.
  const paneler = {
    sensorer: sensorpanel,
    kart: kartpanel,
    bunn: bunnpanel,
    kontakter: listepanel,
    kontaktkort: kortpanel,
  };
  const poppet = montérPoppetPanel(rot, paneler, { behold: banner ? [banner] : [] });
  if (poppet === 'ukjent') return;
  /**
   * Er KORTET selv poppet ut, er det hele vinduets innhold, og da kan det ikke
   * skjules av «ingen kontakter»-regelen — da ville vinduet vært helt blankt, som
   * leses som «appen er ødelagt». I det vinduet er «Ingen kontakt valgt» riktig og
   * eneste ærlige svar.
   */
  const kortAlene = poppet === 'panel' && poppetPanel() === 'kontaktkort';

  // ── Tegning ───────────────────────────────────────────────────────────────

  function tegnTopp(): void {
    const post = tilstand.post;
    toppPost.textContent = post ? post.navn : 'Ingen post meldt';
    const t = kilde.tilkobling();
    sett(toppTilkobling, tilkoblingsmerke(t));
    toppKlokke.textContent = formaterKlokke(naaMs);
  }

  /**
   * Tilkoblingsmerket i toppstripa. Typet mot den EKTE unionen nå — casten
   * `(t as { tilstand: string })` er borte sammen med den lokale `demo`-varianten,
   * så en ny tilkoblingstilstand blir en byggefeil her i stedet for å falle stille
   * ned i `default` og bli lest som «Frakoblet».
   */
  function tilkoblingsmerke(t: Tilkobling): HTMLElement {
    switch (t.tilstand) {
      case 'tilkoblet':
        return statusprikk('Tilkoblet', 'frisk');
      case 'ovelse':
        return merke('ØVINGSDATA', 'ovelse');
      case 'kobler':
        return statusprikk('Kobler …', 'treg');
      case 'ikke-konfigurert':
        return statusprikk('Ingen bro satt opp', 'stille');
      default:
        return statusprikk('Frakoblet', 'borte');
    }
  }

  /**
   * Tomtilstanden for «ingen bro» — FUNN B, med hierarkiet snudd.
   *
   * Forklaringen på norsk står først, variabelnavnet står dempet til slutt, og
   * NESTE STEG er en knapp han kan trykke på nå. At øvingsknappen bare finnes i
   * denne grenen er ikke UI-pynt: er broen konfigurert, er øving nektet av porten
   * uansett, og en knapp som ikke kan virke er nettopp «mockup og tull» (§10).
   */
  function ingenBro(t: Extract<Tilkobling, { tilstand: 'ikke-konfigurert' }>): HTMLElement {
    return manglerKilde({
      forklaring: `${t.forklaring} — flata er tom fordi det ikke finnes noen sensorer å vise`,
      hvaKommer:
        'Når KOP-broen melder inn radar, RF, hydrofon og kamera, kommer de opp her — med alder på siste '
        + 'måling per sensor, og med hvilke som IKKE ser en kontakt.',
      neste: t.neste,
      handling: { tekst: 'Start øvingsdata', gjør: () => settOvelse(true) },
      teknisk: t.variabel,
    });
  }

  function tegnSensorer(): void {
    if (tilstand.sensorer.length === 0) {
      const t = kilde.tilkobling();
      sett(
        sensorpanel.kropp,
        t.tilstand === 'ikke-konfigurert'
          ? ingenBro(t)
          : manglerKilde({
              forklaring: 'Ingen sensorer er meldt inn ennå',
              hvaKommer:
                'Når KOP-broen melder inn radar, RF, hydrofon og kamera, kommer de opp her — med alder på '
                + 'siste måling per sensor.',
              // Grunnen til at det IKKE står en øvingsknapp her står ordrett, med
              // porten som kilde. En knapp som mangler uten forklaring leses som
              // en feil i appen.
              neste: 'Broen er konfigurert, så dette er en driftssak: sjekk at KOP-broen kjører på posten.'
                + (ovelsevedtak.nektet ? ` ${ovelsevedtak.nektet}` : ''),
              teknisk: `Forsøkt: ${t.adresse}`,
            }),
      );
      sensorpanel.settUnder('0 av 4');
      return;
    }

    const liste = div('sensorliste');
    const statuser = tilstand.sensorer.map((s) =>
      sensorStatus(s, tilstand.sisteMaalingPerSensor[s.id] ?? null, naaMs),
    );

    tilstand.sensorer.forEach((s, i) => {
      const st = statuser[i];
      const rad = div(`sensorrad sensorrad-${nivaaKlasse(st.nivaa)}`);
      rad.append(span('sensorrad-prikk', ''));
      const høyre = div();
      const linje = div('sensorrad-navn');
      linje.textContent = `${SENSORART_NAVN[s.art]} — ${s.navn}`;
      høyre.append(linje);
      høyre.append(
        span(
          'sensorrad-alder',
          st.alderMs === null
            ? 'Har aldri meldt'
            : `${FRISKHET_NAVN[st.friskhet]} · ${formaterAlder(st.alderMs)}`,
        ),
      );
      // Grunnene ORDRETT fra broen. En oversettelse til vår egen ordliste ville
      // skjult hva som faktisk er galt med sensoren.
      for (const g of st.grunner) høyre.append(div('sensorrad-grunn', g));
      høyre.append(span('sensorrad-alder', NIVAA_NAVN[st.nivaa]));
      rad.append(høyre);
      liste.append(rad);
    });

    sett(sensorpanel.kropp, liste);
    const tillit = postTillit(statuser);
    const friske = statuser.filter((s) => s.nivaa === 'ok').length;
    sensorpanel.settUnder(`${friske} av ${statuser.length} OK · ${versteTekst(tillit)}`);
  }

  /**
   * Postens verste sensortilstand som lesbar tekst.
   *
   * DENNE VAR EN FEIL, OG ROTÅRSAKEN ER VERDT Å HUSKE: funksjonen tok `unknown`
   * og gjettet på feltnavn (`.tekst`, `.nivaa`) som IKKE finnes på `PostTillit`
   * — den har `verste`. Resultatet var at den alltid returnerte «—», så
   * underteksten sto «2 av 4 OK · —» og den verste sensortilstanden ble aldri
   * vist. Defensiv `unknown`-kode mot en type jeg ikke hadde lest gjorde at
   * typesystemet ikke kunne fange det. Nå er typen den ekte, og en slik feil
   * ville brutt bygget.
   */
  function versteTekst(t: PostTillit): string {
    return NIVAA_NAVN[t.verste];
  }

  /**
   * Kontaktlista OG kontaktkortet — ÉN tomtilstand, ikke to (FUNN C).
   *
   * ══ HVA SOM VAR GALT ═══════════════════════════════════════════════════════
   * Uten kontakter viste kontaktområdet to tomme bokser samtidig, rett under
   * hverandre: «Ingen kontakter» i lista og «Ingen kontakt valgt» i kortet. Den
   * andre er ikke bare overflødig — den er MENINGSLØS. «Velg en kontakt i lista
   * over» er et råd om å gjøre noe som ikke er mulig, siden lista er tom, og to
   * bokser tar dobbelt så mye plass som én for å si mindre.
   *
   * ══ REGELEN ════════════════════════════════════════════════════════════════
   * Under-boksene vises FØRST når det finnes noe å velge. Er lista tom, står ÉN
   * tomtilstand som forklarer hva panelet VIL vise — hvilke fire spørsmål kortet
   * svarer på — og kortpanelet skjules helt. Da er «ingen kontakter» hele
   * sannheten, og den står ett sted.
   */
  function tegnKontaktliste(): void {
    const artFor = artOppslag(tilstand);
    const rangert = sorterPaaTrussel(tilstand.kontakter, naaMs, artFor);

    // Kortpanelet er en UNDER-boks av kontaktlista, og det skjules når det ikke
    // finnes en kontakt å ha et kort om. Kartet og sensorstolpen skjules aldri —
    // de er selve flata. Se `kortLukket`/`kortAlene` for de to unntakene.
    const visKort = kortAlene || (!kortLukket && rangert.length > 0);
    kortpanel.vis(visKort);

    if (rangert.length === 0) {
      const t = kilde.tilkobling();
      sett(
        listepanel.kropp,
        tomt(
          'Ingen kontakter ennå',
          t.tilstand === 'ikke-konfigurert'
            ? 'Lista fylles av KOP-broen, som ikke er satt opp ennå — se sensorstolpen til venstre for hva '
              + 'som mangler. Når en kontakt kommer, står den her rangert på trussel, og kontaktkortet under '
              + 'viser hvem som ser den, hva de målte, hvor sikkert det er, og hvem som IKKE ser den.'
            : 'Ingen sensor har meldt en kontakt. Kommer det en, står den her rangert på trussel — og '
              + 'kontaktkortet under viser hvem som ser den, hva de målte, hvor sikkert det er, og hvem som '
              + 'IKKE ser den. Lista viser aldri oppdiktede mål.',
          // Fartøysymbolet er NJORDs eget ikon, så det sendes inn som en ferdig
          // node. Flatens egne navn ('kart', 'kamera' under) kan brukes direkte.
          { ikon: ikon('kontakt') },
        ),
      );
      listepanel.settUnder('0');
      return;
    }

    const liste = div('kontaktliste');
    for (const { kontakt, vurdering } of rangert) {
      const bånd = trusselband(vurdering.poeng);
      const rad = document.createElement('button');
      rad.type = 'button';
      rad.className = `kt kt-${bånd}`;
      rad.setAttribute('aria-pressed', String(kontakt.id === valgtId));
      rad.append(span('kt-navn', kontakt.navn));
      rad.append(span('kt-tall', `${TRUSSELBAND_NAVN[bånd]} ${Math.round(vurdering.poeng)}`));
      // Begrunnelsen i lista, ikke bare i kortet: en rangering ingen kan
      // etterprøve blir ikke stolt på, og da sorterer operatøren i hodet i
      // stedet.
      rad.append(div('kt-grunn', grunnTekst(vurdering)));
      rad.addEventListener('click', () => ctx.velg(kontakt.id));
      liste.append(rad);
    }
    sett(listepanel.kropp, liste);

    const akutte = rangert.filter((r) => trusselband(r.vurdering.poeng) === 'akutt').length;
    listepanel.settUnder(akutte > 0 ? `${rangert.length} · ${akutte} akutt` : `${rangert.length}`);
  }

  /**
   * Begrunnelsen bak trusselpoengene, som lesbar linje. Typet mot den EKTE
   * `Trusselvurdering` av samme grunn som over — `unknown` her ville gjort at et
   * omdøpt felt ble en stille tom streng i stedet for en byggefeil.
   */
  function grunnTekst(v: Trusselvurdering): string {
    return v.bidrag.map((b) => b.navn).join(' + ') || 'Ingen bidrag';
  }

  function tegnKart(): void {
    // Kartet er ikke bygget ennå. En tom flate med en ÆRLIG forklaring er riktig
    // her — et gråtonet plassholderkart ville sett ut som et kart uten data, og
    // operatøren kunne trodd at havet var tomt.
    sett(
      kartpanel.kropp,
      tomt(
        'Sjøkartet er ikke koblet inn ennå',
        'Radaroverlay, peilelinjer fra RF og hydrofon, og kontaktsymboler kommer her. Motoren (@skade/radar) er bygget; kartlaget gjenstår.',
        { ikon: 'kart' },
      ),
    );
    kartpanel.settUnder('under arbeid');
  }

  function tegnBunn(): void {
    const kameraer = tilstand.sensorer.filter((s) => s.art === 'kamera');
    const hydrofoner = tilstand.sensorer.filter((s) => s.art === 'hydrofon');
    sett(
      bunnpanel.kropp,
      tomt(
        'Ingen strøm vises ennå',
        kameraer.length + hydrofoner.length === 0
          ? 'Når et PTZ-kamera eller en hydrofon er meldt inn, kommer videoruten og spektrogram-stripa her.'
          : `${kameraer.length} kamera og ${hydrofoner.length} hydrofon er meldt inn, men strømmene er ikke koblet til klienten ennå.`,
        { ikon: 'kamera' },
      ),
    );
    bunnpanel.settUnder(`${kameraer.length} kamera · ${hydrofoner.length} hydrofon`);
  }

  function tegn(): void {
    naaMs = Date.now();
    tegnTopp();
    tegnSensorer();
    tegnKart();
    tegnBunn();
    tegnKontaktliste();
    // Bare når kortet faktisk er synlig. Ellers ville vi tegnet «Ingen kontakt
    // valgt» inn i et skjult panel hvert sekund — arbeid ingen ser, og en boks som
    // står klar til å blinke fram i det panelet vises igjen.
    if (!kortpanel.rot.hidden) tegnKontaktkort(kortpanel.kropp, ctx);
    for (const l of lyttere) l();
  }

  // ── Abonnementer ──────────────────────────────────────────────────────────

  kilde.påMelding((m) => {
    tilstand = bruk(tilstand, m);
    // En kontakt som forsvinner skal ikke etterlate et valgt kort som viser et
    // mål ingen sensor ser lenger.
    if (valgtId && !tilstand.kontakter.some((k) => k.id === valgtId)) valgtId = null;
    tegn();
  });
  kilde.påTilkobling(() => tegn());

  // Sekundtikk: alderstallene endres uten at det kommer meldinger. Uten dette
  // ville «3 s siden» stått og løyet til neste måling kom.
  const tikk = window.setInterval(tegn, 1000);
  window.addEventListener('beforeunload', () => {
    window.clearInterval(tikk);
    kilde.lukk();
  });

  tegn();
}

// Escape lukker kontaktkortet ved å tømme valget — samme forventning som i COP.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const a = document.activeElement;
  const tag = a?.tagName?.toUpperCase() ?? '';
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
  const valgt = document.querySelector<HTMLElement>('.kt[aria-pressed="true"]');
  valgt?.click();
});

start();
